import { useMemo } from 'react';
import { ProductConstants } from '../../constants/ProductConstants';
import { useAppSelector } from '../../redux/app/hooks';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

export interface DynamicSubItemProps {
    userText?: string;
    prompt: string;
}

export interface DiscoveryCategory {
    id: string;
    name: string;
    items: DynamicSubItemProps[];
}

export interface DiscoveryState {
    isEnabled: boolean;
    categories: DiscoveryCategory[];
}

const CATEGORY_IDS = ['reports', 'application'] as const;

export const useDiscovery = (): DiscoveryState => {
    const hostContext = useAppSelector((state) => state.hostContext.hostContext);
    const { DynamicReport, EnableDiscoveryUi } = useAppSelector((state) => state.app.serviceInfo.featureFlags);
    const { t } = useTranslation('discovery');

    return useMemo(() => {
        // Calculate dates once
        const now = new Date();
        const lastMonth = new Date(now);
        lastMonth.setMonth(now.getMonth() - 1);
        const currentYear = now.getFullYear();

        // Create formatter once
        const monthFormatter = new Intl.DateTimeFormat(i18n.language, { month: 'long' });

        const defaultPlaceholderValues = {
            month: monthFormatter.format(now),
            lastMonth: monthFormatter.format(lastMonth),
            year: currentYear.toString(),
            lastYear: (currentYear - 1).toString(),
            penYear: (currentYear - 2).toString(), // "penultimate" year, i.e. the year before the last year
        };

        const discoveryEnabledProducts = [
            ProductConstants.EAccounting.ProductName.toLowerCase(),
            ProductConstants.CloudPayroll.ProductName.toLowerCase(),
            ProductConstants.Advisor.ProductName.toLowerCase(),
            ProductConstants.ApyVpyETax.ProductNameAPY.toLowerCase(),
        ];
        const selectedProducts = hostContext.products ?? [];
        const isEnabled =
            DynamicReport &&
            EnableDiscoveryUi &&
            selectedProducts.some((product) => discoveryEnabledProducts.includes(product.name.toLowerCase()));

        if (!isEnabled || !selectedProducts.length) {
            return { isEnabled: false, categories: [] };
        }

        const categories = CATEGORY_IDS.map((id) => {
            let items: DynamicSubItemProps[] = [];

            // Aggregate items from all products
            const productItems = selectedProducts.flatMap((product) => {
                const productName = product.name.toLowerCase();
                const items = t(`${id}.${productName}`, { returnObjects: true, ...defaultPlaceholderValues });
                return Array.isArray(items) ? (items as DynamicSubItemProps[]) : [];
            });
            items = [...new Set(productItems)]; // Remove duplicates

            return {
                id,
                name: t(`categories.${id}`),
                items,
            };
        });

        return { isEnabled, categories };
    }, [hostContext.products, DynamicReport, EnableDiscoveryUi, t]);
};
